import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Zap,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Ship,
  Package,
  Percent,
  BarChart2,
  Users,
  DollarSign,
  Receipt,
  TrendingUp,
  Code2,
  Clock,
  GitBranch,
  Shield,
  Users2,
  Timer,
  Repeat,
  FileText,
  Sparkles,
  LayoutDashboard,
  BookOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { useOnboardingStore, type OnboardingSegment } from '../store/onboarding-store';
import { SEGMENTS, SEGMENT_MAP, type SegmentDef } from '../data/segments';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { authService } from '@/features/auth/services/auth-service';
import { esEmpresaDemo } from '@/features/mini-prixia/empresa-demo';
import { useHayMomentoAbierto } from '@/features/mini-prixia/momento-abierto';

// Map icon string names to actual components
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Package,
  Percent,
  BarChart2,
  Users,
  Ship,
  DollarSign,
  Receipt,
  TrendingUp,
  Code2,
  Clock,
  GitBranch,
  Shield,
  Users2,
  Timer,
  Repeat,
  FileText,
};

// ── Step indicators ──────────────────────────────────────────────

function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-2 justify-center">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={cn(
            'h-1.5 rounded-full transition-all duration-300',
            i === current
              ? 'w-6 bg-primary'
              : i < current
                ? 'w-2 bg-primary/40'
                : 'w-2 bg-muted-foreground/20'
          )}
        />
      ))}
    </div>
  );
}

// ── Step 0: Bienvenida ───────────────────────────────────────────

function StepBienvenida({ onNext, onSkip }: { onNext: () => void; onSkip: () => void }) {
  const { user } = useAuthStore();
  const firstName = user?.firstName || 'Bienvenido';

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="text-center space-y-3 pt-2">
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
          className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center"
        >
          <Zap className="w-8 h-8 text-primary" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-2xl font-bold tracking-tight">Hola, {firstName} 👋</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Bienvenido a <strong className="text-foreground">Prixia</strong>
          </p>
        </motion.div>
      </div>

      {/* Value props */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="space-y-3"
      >
        {[
          {
            icon: <Sparkles className="w-4 h-4 text-violet-500" />,
            title: 'Cotizaciones inteligentes',
            desc: 'Modelos de pricing con fórmulas automáticas y cálculo en cascada.',
          },
          {
            icon: <CheckCircle2 className="w-4 h-4 text-green-500" />,
            title: 'Flujo de aprobaciones',
            desc: 'Control de márgenes con workflow configurable de aprobación.',
          },
        ].map((item, i) => (
          <div
            key={i}
            className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 border border-border/50"
          >
            <div className="mt-0.5 flex-shrink-0">{item.icon}</div>
            <div>
              <p className="text-sm font-medium">{item.title}</p>
              <p className="text-xs text-muted-foreground">{item.desc}</p>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="flex items-center justify-between pt-2"
      >
        <button
          onClick={onSkip}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Saltar tutorial
        </button>
        <Button onClick={onNext} className="gap-2">
          Personalizar mi experiencia
          <ChevronRight className="w-4 h-4" />
        </Button>
      </motion.div>
    </div>
  );
}

// ── Step 1: Selector de segmento ─────────────────────────────────

function StepSegmento({
  selected,
  onSelect,
  onNext,
  onPrev,
}: {
  selected: OnboardingSegment | null;
  onSelect: (s: OnboardingSegment) => void;
  onNext: () => void;
  onPrev: () => void;
}) {
  return (
    <div className="space-y-5">
      <div className="text-center space-y-1">
        <h2 className="text-xl font-bold">¿Cómo es tu negocio?</h2>
        <p className="text-sm text-muted-foreground">
          Personalizo el tutorial y los ejemplos para tu industria.
        </p>
      </div>

      {/* Segment cards */}
      <div className="grid grid-cols-2 gap-3">
        {SEGMENTS.map((seg) => {
          const isSelected = selected === seg.id;
          return (
            <button
              key={seg.id}
              onClick={() => onSelect(seg.id)}
              className={cn(
                'relative text-left p-4 rounded-xl border-2 transition-all duration-200 hover:scale-[1.02]',
                isSelected
                  ? `${seg.borderClass} ${seg.bgClass} ring-2 ${seg.ringClass}`
                  : 'border-border bg-background hover:border-muted-foreground/30'
              )}
            >
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-2 right-2"
                >
                  <CheckCircle2 className={cn('w-4 h-4', seg.textClass)} />
                </motion.div>
              )}
              <div className="space-y-2">
                <div
                  className={cn(
                    'w-9 h-9 rounded-lg flex items-center justify-center text-lg',
                    isSelected ? `${seg.bgClass} border ${seg.borderClass}` : 'bg-muted'
                  )}
                >
                  {seg.emoji}
                </div>
                <div>
                  <p
                    className={cn(
                      'text-sm font-semibold leading-tight',
                      isSelected && seg.textClass
                    )}
                  >
                    {seg.title}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">
                    {seg.subtitle}
                  </p>
                </div>
              </div>
              {/* Examples pills */}
              <div className="mt-2 flex flex-wrap gap-1">
                {seg.examples.slice(0, 2).map((ex) => (
                  <span
                    key={ex}
                    className={cn(
                      'text-[10px] px-1.5 py-0.5 rounded-full border',
                      isSelected
                        ? `${seg.bgClass} ${seg.borderClass} ${seg.textClass}`
                        : 'bg-muted text-muted-foreground border-border'
                    )}
                  >
                    {ex}
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-1">
        <Button variant="outline" onClick={onPrev} className="gap-2">
          <ChevronLeft className="w-4 h-4" />
          Atrás
        </Button>
        <Button onClick={onNext} disabled={!selected} className="gap-2">
          Continuar
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// ── Step 2: Listo ────────────────────────────────────────────────

function StepListo({
  segment,
  onFinish,
  onDashboard,
}: {
  segment: SegmentDef;
  onFinish: () => void;
  onDashboard: () => void;
}) {
  return (
    <div className="space-y-5">
      {/* Success header */}
      <div className="text-center space-y-3">
        <motion.div
          initial={{ scale: 0, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 18 }}
          className={cn(
            'mx-auto w-16 h-16 rounded-2xl border-2 flex items-center justify-center text-3xl',
            segment.bgClass,
            segment.borderClass
          )}
        >
          {segment.emoji}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <h2 className="text-xl font-bold">¡Listo, {segment.tutorialCompany}!</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Tutorial personalizado para{' '}
            <span className={cn('font-semibold', segment.textClass)}>{segment.title}</span>
          </p>
        </motion.div>
      </div>

      {/* Features for this segment */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className={cn('rounded-xl p-4 border space-y-2.5', segment.bgClass, segment.borderClass)}
      >
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Lo que aprenderás
        </p>
        {segment.features.map((feat) => {
          const Icon = ICON_MAP[feat.icon] ?? CheckCircle2;
          return (
            <div key={feat.label} className="flex items-center gap-2.5">
              <div
                className={cn(
                  'w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 bg-background/70',
                  segment.borderClass,
                  'border'
                )}
              >
                <Icon className={cn('w-3.5 h-3.5', segment.textClass)} />
              </div>
              <span className="text-sm">{feat.label}</span>
            </div>
          );
        })}
      </motion.div>

      {/* Scenario preview */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.35 }}
        className="bg-muted/40 rounded-lg p-3 border border-border/50 text-xs text-muted-foreground"
      >
        <span className="font-medium text-foreground">Escenario del tutorial: </span>
        {segment.tutorialScenario}
      </motion.div>

      {/* CTAs */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="flex gap-3 pt-1"
      >
        <Button variant="outline" onClick={onDashboard} className="flex-1 gap-2">
          <LayoutDashboard className="w-4 h-4" />
          Ir al Dashboard
        </Button>
        <Button onClick={onFinish} className="flex-1 gap-2">
          <BookOpen className="w-4 h-4" />
          Ver Tutorial
        </Button>
      </motion.div>
    </div>
  );
}

// ── Main Onboarding Modal ────────────────────────────────────────

export function OnboardingModal() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const { completed, segment, step, setSegment, nextStep, prevStep, complete, skip } =
    useOnboardingStore();

  const [localSegment, setLocalSegment] = useState<OnboardingSegment | null>(segment);
  const hayMomento = useHayMomentoAbierto();

  // Ronda V (V1): en una empresa demo la entrada la guían «Tus misiones», no esta bienvenida.
  if (
    !isAuthenticated ||
    completed ||
    (user as any)?.onboardingCompleted ||
    esEmpresaDemo() ||
    hayMomento
  )
    return null;

  const markDone = () => authService.completeOnboarding().catch(() => {});

  const handleSkip = () => {
    skip();
    markDone();
  };

  const handleStep0Next = () => nextStep();

  const handleStep1Select = (s: OnboardingSegment) => {
    setLocalSegment(s);
    setSegment(s);
  };

  const handleStep1Next = () => {
    if (!localSegment) return;
    setSegment(localSegment);
    nextStep();
  };

  const handleStep2Tutorial = () => {
    complete();
    markDone();
    navigate('/tutorial');
  };

  const handleStep2Dashboard = () => {
    complete();
    markDone();
    navigate('/dashboard');
  };

  const currentSegmentDef = localSegment ? SEGMENT_MAP[localSegment] : null;

  const TOTAL_STEPS = 3;

  return (
    // Backdrop
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Blurred overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={handleSkip}
      />

      {/* Modal card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className="relative z-10 w-full max-w-md bg-background border border-border rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Top bar */}
        <div className="px-6 pt-5 pb-0 flex items-center justify-between">
          <StepDots current={step} total={TOTAL_STEPS} />
          <button
            onClick={handleSkip}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Saltar
          </button>
        </div>

        {/* Step content with animation */}
        <div className="px-6 pb-6 pt-4">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div
                key="step-0"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <StepBienvenida onNext={handleStep0Next} onSkip={handleSkip} />
              </motion.div>
            )}
            {step === 1 && (
              <motion.div
                key="step-1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <StepSegmento
                  selected={localSegment}
                  onSelect={handleStep1Select}
                  onNext={handleStep1Next}
                  onPrev={prevStep}
                />
              </motion.div>
            )}
            {step === 2 && currentSegmentDef && (
              <motion.div
                key="step-2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                <StepListo
                  segment={currentSegmentDef}
                  onFinish={handleStep2Tutorial}
                  onDashboard={handleStep2Dashboard}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
